import { useEffect, useMemo, useState } from "react";
import { Autocomplete, Fade } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectActiveRobotVersion } from "@/store/ses/sesRobotSlice";
import { changeConnectorName, changeStringValuesNatsConnector, getHandlerList, selectHandlerList } from "@/store/ses/sesConnectorSlice";
import { useAccessRight, useTranslate } from "@/hooks";
import { SES } from "@/constants/accessRights";
import { selectDataServers } from "@/store/serverSlice";
import { RenderOption, CustomTextField, H3 } from "@/ui";
import { INatsProps } from "./Nats.props";
import styles from "./Nats.module.scss";

const Nats = ({ selectId, channel, connectorName, settings, changeFlgHandler }: INatsProps): JSX.Element => {
	const [inputConnectorName, setInputConnectorName] = useState<string>(""); // название коннектора
	const [inputHost, setInputHost] = useState<string>(""); // адрес сервера
	const [inputPort, setInputPort] = useState<string>(""); // порт адреса сервера
	// const [inputInboundSubject, setInputInboundSubject] = useState<string>(""); // канал входящих сообщений
	// const [inputOutboundSubject, setInputOutboundSubject] = useState<string>(""); // канал исходящих сообщений
	const [inputHandler, setInputHandler] = useState<string>(""); // обработчик
	const [inputServer, setInputServer] = useState<string>(""); // обслуживающий сервер

	const dispatch = useAppDispatch();
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const handlerList = useAppSelector(selectHandlerList); // store - список обработчиков
	const dataServers = useAppSelector(selectDataServers); // store - данные о серверах

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	const isDisabled = useMemo(() => {
		return !isAccess(SES.CONNECTOR_MODIFY) || activeRobotVersion === "backup";
	}, [activeRobotVersion]);

	useEffect(() => {
		isAccess(SES.HANDLER_LIST) && handlerList.data.length === 0 && dispatch(getHandlerList("nats")); // получаем список обработчиков
	}, []);

	// следим за id - вписываем значения
	useEffect(() => {
		setInputConnectorName(connectorName);
		setInputHost(settings.host);
		// setInputInboundSubject(settings.inboundSubject);
		// setInputOutboundSubject(settings.outboundSubject);
		setInputHandler(settings.handler);
		setInputServer(settings.server);
		setInputPort(settings.port);
	}, [selectId, connectorName, channel, settings]);

	// обработчик изменения названия коннектора
	const connectorNameChangeHandler = (): void => {
		if (connectorName === inputConnectorName) return;
		dispatch(changeConnectorName({ newName: inputConnectorName }));
		changeFlgHandler("name");
	};

	// обработчик изменения адреса сервера
	const hostChangeHandler = (): void => {
		if (settings.host === inputHost) return;
		dispatch(changeStringValuesNatsConnector({ channel, field: "host", value: inputHost }));
		changeFlgHandler("params");
	};

	// обработчик изменения канала входящих сообщений
	// const inboundSubjectChangeHandler = (): void => {
	// 	if (settings.inboundSubject === inputInboundSubject) return;
	// 	dispatch(changeStringValuesNatsConnector({ channel, field: "inboundSubject", value: inputInboundSubject }));
	// 	changeFlgHandler("params");
	// };

	// обработчик изменения канала исходящих сообщений
	// const outboundSubjectChangeHandler = (): void => {
	// 	if (settings.outboundSubject === inputOutboundSubject) return;
	// 	dispatch(changeStringValuesNatsConnector({ channel, field: "outboundSubject", value: inputOutboundSubject }));
	// 	changeFlgHandler("params");
	// };

	// обработчик изменения обработчика
	const handlerChangeHandler = (): void => {
		if (settings.handler === inputHandler) return;
		dispatch(changeStringValuesNatsConnector({ channel, field: "handler", value: inputHandler }));
		changeFlgHandler("params");
	};

	// обработчик изменения сервера
	const serverChangeHandler = (): void => {
		if (settings.server === inputServer) return;
		dispatch(changeStringValuesNatsConnector({ channel, field: "server", value: inputServer }));
		changeFlgHandler("params");
	};

	const portChangeHandler = (): void => {
		if (settings.port === inputPort) return;
		dispatch(changeStringValuesNatsConnector({ channel, field: "port", value: inputPort }));
		changeFlgHandler("params");
	};

	return (
		<Fade in={true} timeout={500}>
			<div className={styles.container}>
				{/* id клннектора */}
				<H3 className={styles.header}>{`ID: ${selectId}`}</H3>
				{/* общие настройки */}
				<H3 className={styles.header}>{translate("title_generalSettings")}</H3>
				<div className={styles.containerInner}>
					{/* название коннектора */}
					<CustomTextField
						fullWidth
						required
						label={translate("input_connectorName")}
						value={inputConnectorName}
						onChange={(e) => setInputConnectorName(e.target.value)}
						onBlur={connectorNameChangeHandler}
						error={!inputConnectorName}
						disabled={isDisabled}
						hint={"hint_connectorsTitle"}
						hintPosition="left"
					/>
					{/* адрес сервера */}
					<CustomTextField
						fullWidth
						required
						label={translate("input_serverAddress")}
						value={inputHost}
						onChange={(e) => setInputHost(e.target.value)}
						onBlur={hostChangeHandler}
						error={!inputHost}
						disabled={isDisabled}
					/>
					{/* порт */}
					<CustomTextField
						required
						label={translate("input_serverAddressPort")}
						value={inputPort}
						onChange={(e) => setInputPort(e.target.value)}
						onBlur={portChangeHandler}
						error={!inputPort}
						disabled={isDisabled}
						sx={{ width: "370px" }}
					/>
				</div>

				<div className={styles.containerInner}>
					{/* канал входящих сообщений */}
					{/* <CustomTextField
						required
						label={translate("input_incomingMessageChannel")}
						value={inputInboundSubject}
						onChange={(e) => setInputInboundSubject(e.target.value)}
						onBlur={inboundSubjectChangeHandler}
						error={!inputInboundSubject}
						disabled={isDisabled}
					/> */}
					{/* канал исходящих сообщений */}
					{/* <CustomTextField
						required
						label={translate("input_outgoingMessageChannel")}
						value={inputOutboundSubject}
						onChange={(e) => setInputOutboundSubject(e.target.value)}
						onBlur={outboundSubjectChangeHandler}
						error={!inputOutboundSubject}
						disabled={isDisabled}
					/> */}
					{/* обработчик */}
					<Autocomplete
						fullWidth
						freeSolo
						options={Object.values(dataServers?.names || {})}
						value={inputServer}
						onChange={(_, value) => setInputServer(value ? value : "")}
						onBlur={serverChangeHandler}
						disabled={isDisabled}
						renderInput={(params) => (
							<CustomTextField {...params} required label={translate("input_servingServer")} onChange={(e) => setInputServer(e.target.value)} error={!inputServer} />
						)}
						renderOption={(props, option) => {
							return (
								<RenderOption {...props} key={option}>
									{option}
								</RenderOption>
							);
						}}
					/>
					<Autocomplete
						freeSolo
						fullWidth
						options={handlerList.data.map((handler) => handler.name)}
						value={inputHandler}
						onChange={(_, value) => setInputHandler(value ? value : "")}
						onBlur={handlerChangeHandler}
						disabled={isDisabled}
						renderInput={(params) => <CustomTextField {...params} label={translate("input_handler")} onChange={(e) => setInputHandler(e.target.value)} />}
						renderOption={(props, option) => {
							return (
								<RenderOption {...props} key={option}>
									{option}
								</RenderOption>
							);
						}}
					/>
				</div>
			</div>
		</Fade>
	);
};

export default Nats;
